import { useState, useEffect } from 'react';

export default function Hero() {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-white pt-20">
      {/* Parallax Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute top-0 right-0 w-96 h-96 bg-[#2563EB]/5 rounded-full blur-3xl notion-parallax"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        ></div>
        <div 
          className="absolute -bottom-20 -left-20 w-96 h-96 bg-[#0EA5E9]/5 rounded-full blur-3xl notion-parallax"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        ></div>
      </div>

      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20futuristic%20technology%20background%20with%20floating%20digital%20documents%20transforming%20into%20glowing%20blue%20data%20particles%20and%20neural%20network%20connections%2C%20clean%20minimalist%20design%20with%20royal%20blue%20and%20white%20color%20scheme%2C%20professional%20corporate%20aesthetic%2C%20high-tech%20visualization%20of%20document%20processing%20and%20artificial%20intelligence%2C%20geometric%20patterns%20and%20light%20rays%2C%20modern%20industrial%20tech%20style%2C%20pristine%20white%20background%20with%20blue%20accents&width=1920&height=1080&seq=docai-hero-bg-001&orientation=landscape"
          alt="DocAI Technology"
          className="w-full h-full object-cover object-top"
          style={{ transform: `translateY(${scrollY * 0.2}px)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-white/40"></div>
      </div>

      {/* Content Container */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-8 py-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="max-w-[600px]">
            {/* Badge */}
            <div className="notion-fade-in inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-[#2563EB] bg-white/90 backdrop-blur-sm shadow-sm mb-10 hover:shadow-md smooth-transition">
              <div className="w-2 h-2 bg-[#10B981] rounded-full notion-pulse"></div>
              <span className="text-[#2563EB] text-sm font-semibold whitespace-nowrap">Yapay Zeka Destekli Doğrulama</span>
            </div>

            {/* Headline */}
            <h2 className="notion-fade-in text-7xl font-black text-[#1E293B] leading-[1.1] mb-8" style={{ fontFamily: 'Playfair Display, serif', animationDelay: '0.1s' }}>
              İthalat ve Üretim Operasyonlarında 'Sıfır Hata' Dönemi
            </h2>

            {/* Sub-headline */}
            <p className="notion-fade-in text-xl text-[#64748B] leading-relaxed mb-12 max-w-[540px]" style={{ animationDelay: '0.2s' }}>
              Gümrük beyannameleri, çeki listeleri ve stok girişleri arasındaki tutarsızlıkları RAG teknolojisi ile saniyeler içinde yakalayın. Manuel hatalara ve gümrük cezalarına son verin.
            </p>

            {/* CTA Buttons */}
            <div className="notion-fade-in flex flex-wrap gap-4" style={{ animationDelay: '0.3s' }}>
              <button 
                onClick={() => window.open('https://wa.me/905370102404?text=Merhaba%2C%20DocAI%20demosunu%20g%C3%B6rmek%20istiyorum', '_blank')}
                className="bg-[#2563EB] text-white px-10 py-4 rounded-xl text-lg font-semibold hover:bg-[#1d4ed8] hover:-translate-y-1 smooth-transition shadow-lg hover:shadow-xl whitespace-nowrap group cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  DocAI Demosunu İncele
                  <i className="ri-arrow-right-line group-hover:translate-x-1 smooth-transition"></i>
                </span>
              </button>
              <button className="bg-white text-[#2563EB] px-10 py-4 rounded-xl text-lg font-semibold border-2 border-[#2563EB] hover:bg-[#2563EB] hover:text-white hover:-translate-y-1 smooth-transition whitespace-nowrap group">
                <span className="flex items-center gap-2">
                  Risk Analizi Yap
                  <i className="ri-line-chart-line group-hover:translate-x-1 smooth-transition"></i>
                </span>
              </button>
            </div>
          </div>

          {/* Right Visual - Floating Metrics */}
          <div className="hidden lg:flex justify-center items-center relative h-full">
            <div className="relative w-full max-w-sm">
              {/* Floating Card 1 */}
              <div 
                className="absolute top-0 right-0 bg-white rounded-2xl p-4 shadow-lg max-w-xs border border-[#E2E8F0] notion-scale-in hover:shadow-xl smooth-transition"
                style={{ animation: 'notionFloat 6s ease-in-out infinite' }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-[#10B981]/20 flex items-center justify-center">
                    <i className="ri-check-double-line text-[#10B981]"></i>
                  </div>
                  <span className="font-semibold text-[#1E293B] text-sm">%99.8 Doğruluk</span>
                </div>
                <p className="text-xs text-[#64748B]">Gümrük belgeleri saniyeler içinde</p>
              </div>

              {/* Floating Card 2 */}
              <div 
                className="absolute top-32 -left-8 bg-white rounded-2xl p-4 shadow-lg max-w-xs border border-[#E2E8F0] notion-scale-in hover:shadow-xl smooth-transition"
                style={{ animation: 'notionFloat 6s ease-in-out infinite 1s' }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-[#2563EB]/20 flex items-center justify-center">
                    <i className="ri-database-2-line text-[#2563EB]"></i>
                  </div>
                  <span className="font-semibold text-[#1E293B] text-sm">0 Hata</span>
                </div>
                <p className="text-xs text-[#64748B]">Hatalı veri otomatik engelleniyor</p>
              </div>

              {/* Floating Card 3 */}
              <div 
                className="absolute -bottom-8 right-8 bg-white rounded-2xl p-4 shadow-lg max-w-xs border border-[#E2E8F0] notion-scale-in hover:shadow-xl smooth-transition"
                style={{ animation: 'notionFloat 6s ease-in-out infinite 2s' }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-[#F59E0B]/20 flex items-center justify-center">
                    <i className="ri-shield-alert-line text-[#F59E0B]"></i>
                  </div>
                  <span className="font-semibold text-[#1E293B] text-sm">10 Saniye</span>
                </div>
                <p className="text-xs text-[#64748B]">Tüm işlemler tamamlanıyor</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
